import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  Mail,
  Loader2,
  LogOut,
  FileText,
  MessageSquare,
  Eye,
  Trash2,
  Plus,
  Edit,
  Check,
  X,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import {
  adminLogin,
  adminLogout,
  verifyAdminToken,
  getContactRequests,
  markContactAsRead,
  deleteContactRequest,
  getPublications,
  createPublication,
  updatePublication,
  deletePublication,
} from '../services/api';

// Login Component
const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await adminLogin(password);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-teal-700" />
          </div>
          <CardTitle>Administration</CardTitle>
          <CardDescription>Accès réservé au Dr LOUNIS KHODJA</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-teal-700 hover:bg-teal-800"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Se connecter
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Contact Requests Tab
const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    try {
      const data = await getContactRequests();
      setContacts(data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await markContactAsRead(id);
      setContacts(contacts.map(c => c.id === id ? { ...c, is_read: true } : c));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContactRequest(id);
      setContacts(contacts.filter(c => c.id !== id));
      setSelectedContact(null);
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = contacts.filter(c => !c.is_read).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium">Demandes de contact</h2>
          <p className="text-sm text-gray-500">
            {contacts.length} demande(s) au total, {unreadCount} non lue(s)
          </p>
        </div>
      </div>

      {contacts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune demande de contact</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 space-y-2">
            {contacts.map((contact) => (
              <Card
                key={contact.id}
                className={`cursor-pointer transition-all ${
                  selectedContact?.id === contact.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'hover:border-gray-300'
                } ${!contact.is_read ? 'border-l-4 border-l-teal-500' : ''}`}
                onClick={() => setSelectedContact(contact)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${!contact.is_read ? 'text-teal-800' : 'text-gray-900'}`}>
                        {contact.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{contact.subject}</p>
                    </div>
                    {!contact.is_read && (
                      <Badge className="bg-teal-100 text-teal-800 ml-2">Nouveau</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selectedContact ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{selectedContact.name}</CardTitle>
                      <CardDescription>{selectedContact.subject}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {!selectedContact.is_read && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsRead(selectedContact.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Marquer lu
                        </Button>
                      )}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Supprimer cette demande ?</DialogTitle>
                            <DialogDescription>
                              Cette action est irréversible.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="outline">Annuler</Button>
                            </DialogClose>
                            <Button
                              variant="destructive"
                              onClick={() => handleDelete(selectedContact.id)}
                            >
                              Supprimer
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <a href={`mailto:${selectedContact.email}`} className="ml-2 text-teal-700 hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                    {selectedContact.phone && (
                      <div>
                        <span className="text-gray-500">Téléphone:</span>
                        <a href={`tel:${selectedContact.phone}`} className="ml-2 text-teal-700 hover:underline">
                          {selectedContact.phone}
                        </a>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="text-gray-500">Reçu le:</span>
                      <span className="ml-2">{formatDate(selectedContact.created_at)}</span>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Message:</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <ChevronRight className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Sélectionnez une demande pour voir les détails</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Publications Tab
const PublicationsTab = () => {
  const [publications, setPublications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPub, setEditingPub] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    published: true,
  });

  const fetchPublications = async () => {
    try {
      const data = await getPublications(false);
      setPublications(data);
    } catch (err) {
      console.error('Error fetching publications:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPublications();
  }, []);

  const resetForm = () => {
    setFormData({ title: '', excerpt: '', content: '', category: '', published: true });
    setEditingPub(null);
    setIsCreating(false);
  };

  const handleEdit = (pub) => {
    setFormData({
      title: pub.title,
      excerpt: pub.excerpt,
      content: pub.content,
      category: pub.category,
      published: pub.published,
    });
    setEditingPub(pub);
    setIsCreating(false);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPub) {
        const updated = await updatePublication(editingPub.id, formData);
        setPublications(publications.map(p => p.id === editingPub.id ? updated : p));
      } else {
        const created = await createPublication(formData);
        setPublications([created, ...publications]);
      }
      resetForm();
    } catch (err) {
      console.error('Error saving publication:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePublication(id);
      setPublications(publications.filter(p => p.id !== id));
      if (editingPub?.id === id) resetForm();
    } catch (err) {
      console.error('Error deleting publication:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium">Publications</h2>
          <p className="text-sm text-gray-500">{publications.length} article(s)</p>
        </div>
        <Button onClick={handleCreate} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel article
        </Button>
      </div>

      {(isCreating || editingPub) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingPub ? 'Modifier l\'article' : 'Nouvel article'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    minLength={5}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Catégorie *</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    placeholder="Chirurgie biliaire, Proctologie..."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt">Résumé *</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  minLength={10}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Contenu *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  minLength={50}
                  rows={8}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="published"
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                />
                <Label htmlFor="published">Publié</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-teal-700 hover:bg-teal-800">
                  <Check className="w-4 h-4 mr-2" />
                  {editingPub ? 'Enregistrer' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {publications.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune publication</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {publications.map((pub) => (
            <Card key={pub.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{pub.title}</h3>
                      {!pub.published && (
                        <Badge variant="secondary">Brouillon</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{pub.category}</p>
                    <p className="text-sm text-gray-600">{pub.excerpt}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(pub)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Supprimer cet article ?</DialogTitle>
                          <DialogDescription>
                            Cette action est irréversible.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Annuler</Button>
                          </DialogClose>
                          <Button variant="destructive" onClick={() => handleDelete(pub.id)}>
                            Supprimer
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Page
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await verifyAdminToken();
      setIsAuthenticated(isValid);
      setIsChecking(false);
    };
    checkAuth();
  }, []);

  const handleLogout = () => {
    adminLogout();
    setIsAuthenticated(false);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Administration</h1>
              <p className="text-xs text-gray-500">Dr LOUNIS KHODJA</p>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                Voir le site
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="contacts">
          <TabsList className="mb-8">
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Demandes de contact
            </TabsTrigger>
            <TabsTrigger value="publications" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Publications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <ContactsTab />
          </TabsContent>

          <TabsContent value="publications">
            <PublicationsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
